import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import api from "@/lib/api";
import { toast } from "sonner";
import { Check, PlayCircle, FileText, Download } from "lucide-react";
import { downloadDocument, prettySize } from "@/lib/download";
import { ReadAloud } from "@/components/ReadAloud";
import { LessonNotes } from "@/components/LessonNotes";

export default function ModuleView() {
  const { id } = useParams();
  const [search] = useSearchParams();
  const [mod, setMod] = useState(null);
  const [active, setActive] = useState(() => Math.max(0, parseInt(search.get("lesson"), 10) || 0));
  const [done, setDone] = useState({}); // {lessonIndex: true}
  const [docs, setDocs] = useState([]);

  useEffect(() => {
    setActive(Math.max(0, parseInt(search.get("lesson"), 10) || 0));
    api.get(`/modules/${id}`).then((r) => {
      setMod(r.data);
      setActive(a => Math.min(a, Math.max(0, (r.data.lessons?.length || 1) - 1)));
    });
    api.get("/documents", { params: { module_id: id } }).then((r) => setDocs(r.data)).catch(() => {});
    api.get("/progress").then((r) => {
      const map = {};
      r.data.items.filter(i => i.module_id === id && i.completed).forEach(i => { map[i.lesson_index] = true; });
      setDone(map);
    });
  }, [id]);

  const markComplete = async () => {
    await api.post("/progress", { module_id: id, lesson_index: active, completed: true });
    setDone((d) => ({ ...d, [active]: true }));
    toast.success("Lesson complete");
  };

  if (!mod) return <div className="text-sm text-neutral-500">Loading…</div>;
  const lesson = mod.lessons?.[active];
  const totalDone = Object.keys(done).length;
  const total = mod.lessons?.length || 0;

  return (
    <div className="grid lg:grid-cols-12 gap-10">
      <aside className="lg:col-span-4">
        <Link to={`/stage/${mod.stage}`} className="label-eyebrow" data-testid="back-stage">← {mod.stage}</Link>
        <h1 className="font-display text-3xl mt-3 leading-tight">{mod.title}</h1>
        <p className="text-sm text-neutral-500 mt-2">{mod.summary}</p>

        <div className="mt-6 progress-hairline"><div className="fill" style={{transform:`scaleX(${total?totalDone/total:0})`}} /></div>
        <div className="mt-2 text-xs text-neutral-500 font-mono">{totalDone}/{total} complete</div>

        <ol className="mt-8 space-y-1">
          {mod.lessons?.map((l, i) => (
            <li key={i}>
              <button
                onClick={() => setActive(i)}
                data-testid={`lesson-item-${i}`}
                className={`w-full text-left px-4 py-3 border-l-2 transition-colors flex items-start gap-3
                  ${active===i ? "border-[color:var(--brand-primary)] bg-[color:var(--brand-surface2)]" : "border-transparent hover:bg-[color:var(--brand-surface2)]/60"}`}
              >
                <div className={`mt-0.5 w-5 h-5 border hairline flex items-center justify-center ${done[i] ? "bg-[color:var(--brand-primary)] border-[color:var(--brand-primary)]" : ""}`}>
                  {done[i] && <Check size={12} className="text-white" strokeWidth={2}/>}
                </div>
                <div className="flex-1">
                  <div className="text-sm font-medium leading-tight">{l.title}</div>
                  <div className="text-xs text-neutral-500 mt-0.5">
                    {l.kind === "video" ? <PlayCircle size={11} className="inline mr-1"/> : <FileText size={11} className="inline mr-1"/>}
                    {l.duration_min} min
                  </div>
                </div>
              </button>
            </li>
          ))}
        </ol>

        {docs.length > 0 && (
          <div className="mt-8" data-testid="module-documents">
            <div className="label-eyebrow mb-3">Documents</div>
            <div className="space-y-2">
              {docs.map(d => (
                <button
                  key={d.id}
                  onClick={() => downloadDocument(d)}
                  data-testid={`module-doc-${d.id}`}
                  className="w-full text-left border hairline bg-white px-3 py-2 flex items-center gap-3 hover:bg-[color:var(--brand-surface2)] transition-colors"
                >
                  <FileText size={15} className="text-[var(--brand-primary)] shrink-0" />
                  <span className="flex-1 min-w-0 text-sm truncate">{d.title}</span>
                  <span className="text-xs text-neutral-400 font-mono">{prettySize(d.size)}</span>
                  <Download size={13} className="text-neutral-400" />
                </button>
              ))}
            </div>
          </div>
        )}
      </aside>

      <article className="lg:col-span-8">
        {lesson && (
          <>
            <div className="label-eyebrow">{lesson.kind}</div>
            <h2 className="font-display text-4xl md:text-5xl mt-3 leading-[1.05]">{lesson.title}</h2>
            <div className="mt-2 text-sm text-neutral-500">{lesson.duration_min} min read</div>

            {(lesson.body || "").trim() && <ReadAloud moduleId={id} lessonIndex={active} />}

            {lesson.kind === "video" && lesson.media_url && (
              <div className="mt-8 aspect-video w-full border hairline overflow-hidden bg-black">
                {lesson.media_url.includes("/api/files/") ? (
                  <video controls src={lesson.media_url} title={lesson.title} className="w-full h-full" data-testid="lesson-video-player" />
                ) : (
                  <iframe src={lesson.media_url} title={lesson.title} allowFullScreen className="w-full h-full"/>
                )}
              </div>
            )}

            <div className="prose-editorial mt-10">
              <ReactMarkdown>{lesson.body || ""}</ReactMarkdown>
            </div>

            <LessonNotes moduleId={id} lessonIndex={active} />

            <div className="mt-12 flex items-center gap-4">
              <button className="btn-primary" onClick={markComplete} data-testid="lesson-complete-btn" disabled={done[active]}>
                {done[active] ? "Completed" : "Mark complete"}
              </button>
              {active < (mod.lessons.length - 1) && (
                <button className="btn-ghost" onClick={() => setActive(active+1)} data-testid="lesson-next-btn">
                  Next lesson →
                </button>
              )}
            </div>
          </>
        )}
      </article>
    </div>
  );
}
